import React, {createContext, useContext} from "react";
import Notification from "./Notification";
import {NotificationsContainer} from "./NotificationsContainer";
import {useNotificationState} from "../../hooks/useNotificationState";

interface NotificationData {
    id: string;
    type: 'success' | 'error' | 'info' | 'warning';
    title: string;
    message: string;
    duration?: number;
}

interface NotificationsContextType {
    addNotification: (notification: Omit<NotificationData, 'id'>) => void;
    removeNotification: (id: string) => void;
    favoriteAdded: (itemName: string) => void;
    favoriteRemoved: (itemName: string) => void;
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

export const NotificationsProvider: React.FC<{ children: React.ReactNode }> = ({children}) => {
    const {notifications, addNotification, removeNotification} = useNotificationState();

    const favoriteAdded = (itemName: string) => {
        addNotification({
            type: 'success',
            title: 'Добавлено в избранное',
            message: `"${itemName}" добавлен в избранное`,
            duration: 5000
        });
    };

    const favoriteRemoved = (itemName: string) => {
        addNotification({
            type: 'success',
            title: 'Удалено из избранного',
            message: `"${itemName}" удален из избранного`,
            duration: 5000
        });
    };

    const NotificationRenderer = () => (
        <NotificationsContainer>
            {notifications.map(n => (
                <Notification
                    key={n.id}
                    {...n}
                    onClose={removeNotification}
                />
            ))}
        </NotificationsContainer>
    );

    return (
        <NotificationsContext.Provider value={{
            addNotification,
            removeNotification,
            favoriteAdded,
            favoriteRemoved
        }}>
            {children}
            <NotificationRenderer/>
        </NotificationsContext.Provider>
    );
};

export const useNotifications = () => {
    const context = useContext(NotificationsContext);
    if (!context) throw new Error('useNotifications must be used within a NotificationsProvider');
    return context;
};

export type {NotificationData};